import type { ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SettingsIcon, ClipboardListIcon, AlertTriangleIcon, MapPinIcon, UploadIcon, PencilIcon, LightbulbIcon, TrophyIcon, ScanLineIcon } from 'lucide-react';
import type { PluginsStatus } from '@cdf/api-client-react';

import type { UserRole } from '@/components/auth/user-provider';
import { SettingsPage } from '@/pages/settings';
import { WorkspacePage } from '@/pages/workspace/workspace-page';
import { ReportDetailPage } from '@/pages/workspace/report-detail';
import { AssetRegisterPage } from '@/pages/workspace/asset-register';
import { ImportWizardPage } from '@/pages/workspace/import-wizard';
import { BoundaryEditorPage } from '@/pages/workspace/boundary-editor';
import { SuggestionsPage } from '@/pages/workspace/suggestions-page';
import { HuntAdminPage } from '@/pages/workspace/hunt-admin';
import { HuntEditorPage } from '@/pages/workspace/hunt-editor';
import { CvTrainingPage } from '@/pages/workspace/cv-training';

export type AppPlugin = 'auth' | 'database' | 'storage' | 'ai';
export type AppRole = UserRole;

export interface RouteNavigation {
  section: string;
  order: number;
}

export interface AppRouteDefinition {
  id: string;
  path: string;
  title: string;
  icon: LucideIcon;
  element: ReactElement;
  navigation?: RouteNavigation;
  requiredPlugin?: AppPlugin;
  requiredRole?: AppRole;
}

/**
 * Single source of truth for application routes, sidebar entries, and
 * breadcrumbs. Add only routes required by the product brief. Plugin-backed
 * routes must declare requiredPlugin so disabled capabilities never mount or
 * appear in navigation.
 */
export const appRouteRegistry = [
  {
    id: 'settings',
    path: '/settings',
    title: 'Settings',
    icon: SettingsIcon,
    element: <SettingsPage />,
    navigation: { section: 'Workspace', order: 20 }
  },
  {
    id: 'workspace',
    path: '/workspace',
    title: 'Workspace',
    icon: ClipboardListIcon,
    element: <WorkspacePage />,
    navigation: { section: 'Staff', order: 30 },
    requiredPlugin: 'auth'
  },
  {
    id: 'report-detail',
    path: '/workspace/reports/:reportId',
    title: 'Report Detail',
    icon: AlertTriangleIcon,
    element: <ReportDetailPage />,
    requiredPlugin: 'auth'
  },
  {
    id: 'assets',
    path: '/workspace/assets',
    title: 'Assets',
    icon: MapPinIcon,
    element: <AssetRegisterPage />,
    navigation: { section: 'Staff', order: 35 },
    requiredPlugin: 'auth'
  },
  {
    id: 'asset-import',
    path: '/workspace/assets/import',
    title: 'Import Assets',
    icon: UploadIcon,
    element: <ImportWizardPage />,
    requiredPlugin: 'auth'
  },
  {
    id: 'suggestions',
    path: '/workspace/suggestions',
    title: 'Suggestions',
    icon: LightbulbIcon,
    element: <SuggestionsPage />,
    navigation: { section: 'Staff', order: 37 },
    requiredPlugin: 'auth'
  },
  {
    id: 'hunt-admin',
    path: '/workspace/hunts',
    title: 'Hunt Admin',
    icon: TrophyIcon,
    element: <HuntAdminPage />,
    navigation: { section: 'Staff', order: 38 },
    requiredPlugin: 'auth'
  },
  {
    id: 'hunt-editor',
    path: '/workspace/hunts/:huntId',
    title: 'Hunt Editor',
    icon: TrophyIcon,
    element: <HuntEditorPage />,
    requiredPlugin: 'auth'
  },
  {
    id: 'cv-training',
    path: '/workspace/cv-training',
    title: 'CV Training',
    icon: ScanLineIcon,
    element: <CvTrainingPage />,
    navigation: { section: 'Staff', order: 39 },
    requiredPlugin: 'auth'
  },
  {
    id: 'boundaries',
    path: '/workspace/boundaries',
    title: 'Boundaries',
    icon: PencilIcon,
    element: <BoundaryEditorPage />,
    navigation: { section: 'Staff', order: 40 },
    requiredPlugin: 'auth'
  }
] satisfies readonly AppRouteDefinition[];

function hasRequiredPlugin(route: AppRouteDefinition, plugins: PluginsStatus | undefined): boolean {
  if (!route.requiredPlugin) return true;
  return plugins?.[route.requiredPlugin].status === 'ready';
}

function hasRequiredRole(route: AppRouteDefinition, role: AppRole | undefined): boolean {
  if (!route.requiredRole) return true;
  return role === route.requiredRole;
}

export function getAvailableRoutes(
  plugins: PluginsStatus | undefined,
  role: AppRole | undefined
): AppRouteDefinition[] {
  return appRouteRegistry.filter(
    (route) => hasRequiredPlugin(route, plugins) && hasRequiredRole(route, role)
  );
}
